import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "sonner";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
  FileSpreadsheet,
  FileText,
  Flame,
  OctagonAlert,
} from "lucide-react";
import { format, addDays, startOfWeek, differenceInDays, parseISO } from "date-fns";
import {
  classifyWorkflowError,
  executionIntelligenceApi,
} from "../services/intelligenceService";
import { DisabledPill } from "../components/workflow/WorkflowStates";
import {
  constraintIcon,
  gateLabel,
  plainBlockingSentence,
} from "../features/readiness/readinessPresentation";

/**
 * The four date layers, never merged. Activity rows stack them so approved
 * plan, actual, forecast, and proposed recovery stay visually distinct.
 */
const LAYERS = [
  { key: "approved", label: "Approved plan", barClass: "bg-blue-500/80" },
  { key: "actual", label: "Actual", barClass: "bg-emerald-500/80" },
  { key: "forecast", label: "Forecast (calculated)", barClass: "bg-amber-500/70" },
  {
    key: "proposed",
    label: "Proposed recovery",
    barClass: "bg-transparent border-2 border-dashed border-violet-400/90",
  },
];

const GanttPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [activities, setActivities] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  const [intelByActivity, setIntelByActivity] = useState(null);
  const [intelClassified, setIntelClassified] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewStart, setViewStart] = useState(startOfWeek(new Date()));
  const [daysToShow] = useState(28);
  const [isDepDialogOpen, setIsDepDialogOpen] = useState(false);
  const [depForm, setDepForm] = useState({
    predecessor_activity_id: "",
    successor_activity_id: "",
    dependency_type: "FS",
    lag_hours: 0,
  });

  const ganttRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, activitiesRes, depsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`),
        axios.get(`${API}/projects/${projectId}/activities`),
        axios.get(`${API}/projects/${projectId}/dependencies`),
      ]);
      setProject(projectRes.data);
      const loadedActivities = activitiesRes.data || [];
      setActivities(loadedActivities);
      setDependencies(depsRes.data || []);

      const firstDatedActivity = loadedActivities.find((a) => a.start_dt_planned || a.start_date_planned);
      const startDate = projectRes.data.start_date_planned || projectRes.data.start_date || firstDatedActivity?.start_dt_planned || firstDatedActivity?.start_date_planned;
      if (startDate) {
        setViewStart(startOfWeek(parseISO(startDate)));
      }
    } catch (error) {
      toast.error("Failed to load project data");
    } finally {
      setLoading(false);
    }

    // Execution intelligence overlays load independently; a switched-off flag
    // must not degrade the approved-plan chart.
    try {
      const intel = await executionIntelligenceApi.projectActivities(projectId);
      const map = {};
      (intel.activities || []).forEach((a) => {
        map[a.activityId] = a;
      });
      setIntelByActivity(map);
      setIntelClassified(null);
    } catch (err) {
      setIntelByActivity(null);
      setIntelClassified(classifyWorkflowError(err));
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId, fetchData]);

  const handleCreateDependency = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/dependencies`,
        depForm
      );
      setDependencies([...dependencies, response.data]);
      setIsDepDialogOpen(false);
      setDepForm({
        predecessor_activity_id: "",
        successor_activity_id: "",
        dependency_type: "FS",
        lag_hours: 0,
      });
      toast.success("Dependency created");
    } catch (error) {
      toast.error("Failed to create dependency");
    }
  };

  const handleDeleteDependency = async (depId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/dependencies/${depId}`);
      setDependencies(dependencies.filter((d) => d.id !== depId));
      toast.success("Dependency removed");
    } catch (error) {
      toast.error("Failed to remove dependency");
    }
  };

  const handleExportPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/projects/${projectId}/export/pdf`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${project?.name || "project"}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF exported");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/projects/${projectId}/export/excel`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${project?.name || "project"}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("CSV exported");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };

  const navigateWeek = (direction) => {
    setViewStart((prev) => addDays(prev, direction * 7));
  };

  /** Bar geometry for an ISO date range within the visible window. */
  const barGeometry = (startISO, endISO) => {
    if (!startISO) return null;
    let start;
    try {
      start = parseISO(startISO);
    } catch {
      return null;
    }
    let end = start;
    if (endISO) {
      try {
        end = parseISO(endISO);
      } catch {
        end = start;
      }
    }
    const offsetDays = differenceInDays(start, viewStart);
    const durationDays = Math.max(1, differenceInDays(end, start) + 1);
    if (offsetDays + durationDays < 0 || offsetDays > daysToShow) return null;
    const left = Math.max(0, (offsetDays / daysToShow) * 100);
    const width = Math.min((durationDays / daysToShow) * 100, 100 - left);
    return { left, width: Math.max(width, 1.5) };
  };

  /** Resolve the four layers' date ranges for one activity. */
  const activityLayers = (activity) => {
    const intel = intelByActivity?.[activity.id];
    const approvedStart = activity.start_dt_planned || activity.start_date_planned;
    const durationHours = activity.duration_hours_planned || (activity.duration_days || 1) * 8;
    const durationDays = Math.max(1, Math.ceil(durationHours / 8));
    let approvedEnd = activity.end_dt_planned || activity.end_date_planned || null;
    if (!approvedEnd && approvedStart) {
      try {
        approvedEnd = addDays(parseISO(approvedStart), durationDays - 1).toISOString();
      } catch {
        approvedEnd = null;
      }
    }

    const actual = intel?.actual;
    const forecast = intel?.calculatedForecast;
    const proposed = intel?.activeRecoveryProposal?.proposedDates;

    return {
      approved: approvedStart ? { start: approvedStart, end: approvedEnd } : null,
      actual: actual?.startDate
        ? { start: actual.startDate, end: actual.finishDate || format(new Date(), "yyyy-MM-dd"), open: !actual.finishDate }
        : null,
      forecast: forecast?.startDate ? { start: forecast.startDate, end: forecast.finishDate } : null,
      proposed: proposed?.startDate ? { start: proposed.startDate, end: proposed.finishDate } : null,
    };
  };

  const getDates = () => {
    const dates = [];
    for (let i = 0; i < daysToShow; i++) {
      dates.push(addDays(viewStart, i));
    }
    return dates;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary text-xl">Loading Gantt...</div>
      </div>
    );
  }

  const dates = getDates();
  const datedActivities = activities.filter((a) => {
    const layers = activityLayers(a);
    return layers.approved || layers.actual || layers.forecast || layers.proposed;
  });
  const todayOffset = differenceInDays(new Date(), viewStart);
  const todayVisible = todayOffset >= 0 && todayOffset < daysToShow;
  const todayLeft = ((todayOffset + 0.5) / daysToShow) * 100;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="gantt-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">
            Gantt Chart
          </h1>
          <p className="text-muted-foreground mt-1">
            {project?.name || "Project Schedule"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="mr-2 h-4 w-4" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            CSV
          </Button>
          <Dialog open={isDepDialogOpen} onOpenChange={setIsDepDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90">
                <LinkIcon className="mr-2 h-4 w-4" />
                Add Dependency
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
              <DialogHeader>
                <DialogTitle>Create Dependency</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateDependency} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Predecessor Activity</Label>
                  <Select
                    value={depForm.predecessor_activity_id}
                    onValueChange={(value) =>
                      setDepForm({ ...depForm, predecessor_activity_id: value })
                    }
                  >
                    <SelectTrigger className="bg-muted/50">
                      <SelectValue placeholder="Select activity" />
                    </SelectTrigger>
                    <SelectContent>
                      {activities.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Successor Activity</Label>
                  <Select
                    value={depForm.successor_activity_id}
                    onValueChange={(value) =>
                      setDepForm({ ...depForm, successor_activity_id: value })
                    }
                  >
                    <SelectTrigger className="bg-muted/50">
                      <SelectValue placeholder="Select activity" />
                    </SelectTrigger>
                    <SelectContent>
                      {activities
                        .filter((a) => a.id !== depForm.predecessor_activity_id)
                        .map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select
                      value={depForm.dependency_type}
                      onValueChange={(value) =>
                        setDepForm({ ...depForm, dependency_type: value })
                      }
                    >
                      <SelectTrigger className="bg-muted/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FS">Finish-to-Start</SelectItem>
                        <SelectItem value="SS">Start-to-Start</SelectItem>
                        <SelectItem value="FF">Finish-to-Finish</SelectItem>
                        <SelectItem value="SF">Start-to-Finish</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Lag (hours)</Label>
                    <Input
                      type="number"
                      value={depForm.lag_hours}
                      onChange={(e) =>
                        setDepForm({
                          ...depForm,
                          lag_hours: parseInt(e.target.value) || 0,
                        })
                      }
                      className="bg-muted/50 font-mono"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDepDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary">
                    Create
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Timeline Navigation */}
      <Card className="stat-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium">
              {format(viewStart, "MMM d, yyyy")} -{" "}
              {format(addDays(viewStart, daysToShow - 1), "MMM d, yyyy")}
            </span>
            <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Layer legend — the four date layers stay distinct, never merged */}
          <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-muted-foreground">
            {LAYERS.map((l) => (
              <span key={l.key} className="inline-flex items-center gap-1.5">
                <span className={`inline-block w-6 h-2 rounded ${l.barClass}`} />
                {l.label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-px h-3 bg-red-400" />
              Today
            </span>
            <span className="inline-flex items-center gap-1">
              <Flame className="w-3 h-3 text-red-400" />
              Critical
            </span>
            <span className="inline-flex items-center gap-1">
              <OctagonAlert className="w-3 h-3 text-red-400" />
              Blocked
            </span>
            {intelClassified?.kind === "disabled" && (
              <DisabledPill label="Actual/forecast/recovery overlays not enabled" />
            )}
            {intelClassified && intelClassified.kind !== "disabled" && (
              <Badge variant="outline" className="bg-amber-500/15 text-amber-400 border-amber-500/25 font-normal">
                Overlays unavailable: {intelClassified.message}
              </Badge>
            )}
          </div>

          {/* Gantt Chart */}
          <div className="overflow-x-auto" ref={ganttRef}>
            {/* Date Headers */}
            <div className="flex border-b border-border min-w-[800px]">
              <div className="w-56 flex-shrink-0 p-2 font-medium border-r border-border">
                Activity
              </div>
              <div className="flex-1 flex relative">
                {dates.map((date, i) => (
                  <div
                    key={i}
                    className={`flex-1 p-1 text-center text-xs border-r border-border/50 ${
                      date.getDay() === 0 || date.getDay() === 6
                        ? "bg-muted/30"
                        : ""
                    }`}
                  >
                    <div className="font-medium">{format(date, "EEE")}</div>
                    <div className="text-muted-foreground">{format(date, "d")}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity Rows */}
            {datedActivities.length > 0 ? (
              datedActivities.map((activity) => {
                const intel = intelByActivity?.[activity.id];
                const layers = activityLayers(activity);
                const critical = intel?.criticalPathStatus === "CRITICAL";
                const negativeFloat =
                  typeof intel?.totalFloat === "number" && intel.totalFloat < 0;
                const blocked = intel?.readinessState === "BLOCKED";
                const constraint = intel?.governingConstraint;
                const ConstraintIcon = constraint ? constraintIcon(constraint.source) : null;
                const blockingSentence = intel ? plainBlockingSentence(intel) : null;
                const hasMarkers = critical || negativeFloat || blocked || constraint;

                return (
                  <div
                    key={activity.id}
                    className="flex border-b border-border/50 min-w-[800px] hover:bg-muted/20"
                  >
                    <div className="w-56 flex-shrink-0 p-2 border-r border-border">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-sm truncate">{activity.name}</span>
                        {hasMarkers && (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 shrink-0 rounded hover:bg-muted/50 px-0.5"
                                title="Constraints & evidence"
                              >
                                {critical && <Flame className="w-3.5 h-3.5 text-red-400" />}
                                {blocked && <OctagonAlert className="w-3.5 h-3.5 text-red-400" />}
                                {ConstraintIcon && (
                                  <ConstraintIcon className="w-3.5 h-3.5 text-amber-400" />
                                )}
                                {negativeFloat && (
                                  <span className="text-[10px] font-mono text-red-400">
                                    {intel.totalFloat}d
                                  </span>
                                )}
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 text-xs space-y-2" align="start">
                              <p className="font-semibold text-sm">{activity.name}</p>
                              {critical && (
                                <p className="text-red-400 flex items-center gap-1">
                                  <Flame className="w-3.5 h-3.5" />
                                  On the critical path
                                </p>
                              )}
                              {negativeFloat && (
                                <p className="text-red-400">
                                  Negative float: {intel.totalFloat} days — later than the plan allows.
                                </p>
                              )}
                              {blockingSentence && (
                                <p className="text-muted-foreground">{blockingSentence}</p>
                              )}
                              {constraint?.name && !blockingSentence && (
                                <p className="text-muted-foreground">
                                  Governing constraint ({constraint.source}): {constraint.name}
                                </p>
                              )}
                              {(intel?.instances || [])
                                .flatMap((i) => i.blockingGates || [])
                                .slice(0, 6)
                                .map((g, i) => (
                                  <p key={i} className="text-muted-foreground">
                                    Gate not cleared: {gateLabel(g)}
                                  </p>
                                ))}
                              {intel?.activeRecoveryProposal && (
                                <p className="text-violet-300/90">
                                  A recovery proposal covers this activity (
                                  {intel.activeRecoveryProposal.projectSlipDays != null
                                    ? `project slip ${intel.activeRecoveryProposal.projectSlipDays}d`
                                    : "impact under review"}
                                  ). Proposed dates are drawn dashed — they are not applied.
                                </p>
                              )}
                              {intel?.milestoneImpact?.atRisk && (
                                <p className="text-red-400">
                                  Milestone at risk
                                  {intel.milestoneImpact.worstSlipDays != null
                                    ? ` — worst slip ${intel.milestoneImpact.worstSlipDays} day(s)`
                                    : ""}
                                </p>
                              )}
                            </PopoverContent>
                          </Popover>
                        )}
                      </div>
                      {intel?.calculatedForecast?.finishVarianceDays ? (
                        <p className="text-[10px] text-amber-400/90">
                          Forecast {intel.calculatedForecast.finishVarianceDays > 0 ? "+" : ""}
                          {intel.calculatedForecast.finishVarianceDays}d vs plan
                        </p>
                      ) : null}
                    </div>
                    <div className="flex-1 relative" style={{ height: "3.5rem" }}>
                      {todayVisible && (
                        <div
                          className="absolute top-0 bottom-0 w-px bg-red-400/70 z-10"
                          style={{ left: `${todayLeft}%` }}
                        />
                      )}
                      {LAYERS.map((layer, idx) => {
                        const range = layers[layer.key];
                        if (!range) return null;
                        const geom = barGeometry(range.start, range.end);
                        if (!geom) return null;
                        return (
                          <div
                            key={layer.key}
                            className={`absolute rounded ${layer.barClass}`}
                            style={{
                              left: `${geom.left}%`,
                              width: `${geom.width}%`,
                              top: `${4 + idx * 12}px`,
                              height: "9px",
                            }}
                            title={`${activity.name} — ${layer.label}: ${range.start?.slice(0, 10)} → ${
                              range.end ? String(range.end).slice(0, 10) : "…"
                            }${range.open ? " (in progress, no actual finish)" : ""}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{activities.length > 0 ? "No activities have planned dates yet. Add planned dates to render them on the chart." : "No activities yet. Add activities from the project workspace to see them here."}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Dependencies List */}
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="font-heading">Dependencies</CardTitle>
        </CardHeader>
        <CardContent>
          {dependencies.length > 0 ? (
            <div className="space-y-2">
              {dependencies.map((dep) => {
                const predecessor = activities.find(
                  (a) => a.id === dep.predecessor_activity_id
                );
                const successor = activities.find(
                  (a) => a.id === dep.successor_activity_id
                );
                return (
                  <div
                    key={dep.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {predecessor?.name || "Unknown"}
                      </span>
                      <span className="text-muted-foreground">→</span>
                      <span className="font-medium">
                        {successor?.name || "Unknown"}
                      </span>
                      <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                        {dep.dependency_type}
                        {dep.lag_hours > 0 && ` +${dep.lag_hours}h`}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteDependency(dep.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      Remove
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">
              No dependencies created yet.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GanttPage;
